import { __decorate, __metadata } from "tslib";
import { Component, Mixins, Prop, Watch } from "vue-property-decorator";
import simplebar from "simplebar-vue";
import { interpret } from "xstate";
import ClientDetailsMixin from "../../ClientDetailsMixin";
import ClientModule from "@/store/modules/client.module";
import teamOperationsMachine from "../../machines/TeamOperationsMachine";
import { TEAM_OPERATIONS_SECTIONS, teamOperationsFieldLabel, teamOperationsSectionHasValues, } from "@/store/models/client/teamOperations";
import SectionLoader from "@/components/widgets/SectionLoader.vue";
import TeamOperationsValueCell from "./TeamOperationsValueCell.vue";
import TeamOperationsCommunicationModal from "./TeamOperationsCommunicationModal.vue";
import TeamOperationsProcessModal from "./TeamOperationsProcessModal.vue";
import TeamOperationsToolsModal from "./TeamOperationsToolsModal.vue";
import TeamOperationsFieldHistoryModal from "./TeamOperationsFieldHistoryModal.vue";
import { lastUpdatedOn, recordedAt, timeOfDay } from "./teamOperationsHelpers";
// Each section's Add / Edit modal
const SECTION_MODALS = {
    communication: TeamOperationsCommunicationModal,
    process: TeamOperationsProcessModal,
    tools: TeamOperationsToolsModal,
};
/**
 * Team detail > Operations: three tabs (Communication Channel, Process, Tools & Technology Stack). Each tab loads and
 * saves on its own and has its own Add / Edit modal, plus each field's history. The selected tab, which tab is
 * loading, the open modal and the open history all live in TeamOperationsMachine; this component renders them.
 */
let TeamOperationsComponent = /** @class */ (() => {
    let TeamOperationsComponent = class TeamOperationsComponent extends Mixins(ClientDetailsMixin) {
        constructor() {
            super(...arguments);
            this.machine = teamOperationsMachine;
            this.machineService = interpret(this.machine, { devTools: true });
            this.state = this.machine.initialState;
        }
        // Details every tab shares (team, editability) - from the latest tab loaded
        get operations() {
            return ClientModule.teamOperations;
        }
        get tabs() {
            const loadingKey = this.state.context.loadingSection;
            return TEAM_OPERATIONS_SECTIONS.map((tab) => {
                const section = this.loadedSection(tab.key);
                const isLoadingThis = tab.key === loadingKey;
                return {
                    key: tab.key,
                    name: tab.name,
                    section,
                    loading: isLoadingThis && this.state.matches("dataInfo.loading"),
                    failed: isLoadingThis && !section && this.state.matches("dataInfo.failed"),
                };
            });
        }
        // b-tabs index <-> the machine's selected section
        get sectionIndex() {
            const index = TEAM_OPERATIONS_SECTIONS.findIndex((tab) => tab.key === this.state.context.selectedSection);
            return index > -1 ? index : 0;
        }
        set sectionIndex(index) {
            const tab = TEAM_OPERATIONS_SECTIONS[index];
            if (tab) {
                this.send({ type: "SELECT_SECTION", section: tab.key });
            }
        }
        get canEdit() {
            return !!this.operations && this.operations.isTeamActive;
        }
        get editTitle() {
            return this.canEdit ? "" : "Reactivate the team to edit its operations";
        }
        get isEditing() {
            return !this.state.matches("editInfo.idle");
        }
        get isSaving() {
            return this.state.matches("editInfo.saving");
        }
        // The section whose modal is open, and its modal component
        get editSection() {
            const key = this.state.context.editSection;
            return this.isEditing && key ? this.loadedSection(key) : null;
        }
        get editModal() {
            return this.editSection && this.operations ? SECTION_MODALS[this.editSection.key] : null;
        }
        // The row whose history is open. Communication includes Media and Frequency together.
        get historyTarget() {
            const target = this.state.context.historyField;
            const section = target ? this.loadedSection(target.section) : null;
            if (!target || !section) {
                return null;
            }
            const attributeIds = target.attributeIds && target.attributeIds.length ? target.attributeIds : [target.attributeId];
            for (const group of section.groups) {
                const row = group.rows.find((r) => r.itemId === target.itemId);
                if (!row) {
                    continue;
                }
                const fields = attributeIds
                    .map((attributeId) => row.fields.find((field) => (field.attributeId || "") === (attributeId || "")))
                    .filter((field) => !!field);
                return fields.length ? { section, row, fields } : null;
            }
            return null;
        }
        get historyLabel() {
            if (!this.historyTarget) {
                return "";
            }
            if (this.historyTarget.section.key === "communication" || this.historyTarget.section.key === "process") {
                return this.historyTarget.row.label;
            }
            return this.fieldLabel(this.historyTarget.row, this.historyTarget.fields[0]);
        }
        // One panel per field, in row order. Records arrive once the history requests finish.
        get historyPanels() {
            if (!this.historyTarget) {
                return [];
            }
            const histories = this.state.matches("historyInfo.showingHistory") ? ClientModule.teamOperationHistory || [] : [];
            return this.historyTarget.fields.map((field) => {
                const history = histories.find((item) => (item.attributeId || "") === (field.attributeId || ""));
                return { name: field.attributeName, field, records: history ? history.records : [] };
            });
        }
        // Per row label: its last updated value (modifiedOn, else createdOn)
        get lastChangedByItem() {
            const latestByItem = {};
            this.tabs.forEach((tab) => (tab.section ? tab.section.groups : []).forEach((group) => group.rows.forEach((row) => row.fields.forEach((field) => {
                const record = field.current;
                const latest = latestByItem[row.itemId];
                if (record && (!latest || lastUpdatedOn(record) > lastUpdatedOn(latest))) {
                    latestByItem[row.itemId] = record;
                }
            }))));
            return latestByItem;
        }
        send(event) {
            this.machineService.send(event);
        }
        hasValues(section) {
            return teamOperationsSectionHasValues(section);
        }
        fieldLabel(row, field) {
            return teamOperationsFieldLabel(row, field);
        }
        changedDate(record) {
            return this.formatClientDate(recordedAt(lastUpdatedOn(record)));
        }
        changedTitle(record) {
            return `${this.changedDate(record)}, ${timeOfDay(recordedAt(lastUpdatedOn(record)))}`;
        }
        // Process history is only Value / Frequency. Tools and Communication show View once any value is recorded.
        historyFields(sectionKey, row) {
            if (sectionKey === "process") {
                return row.fields.filter((field) => field.attributeName === "Value / Frequency");
            }
            if (sectionKey === "tools" || sectionKey === "communication") {
                return row.fields.filter((field) => !!field.current);
            }
            return row.fields;
        }
        showHistory(sectionKey, row) {
            return this.historyFields(sectionKey, row).length > 0;
        }
        startEdit(section) {
            this.send({ type: "EDIT", section: section.key });
        }
        save(dto) {
            this.send({ type: "SAVE", payLoad: dto });
        }
        openHistory(section, row) {
            // Communication shows Media and Frequency in one dialog, including a field that has not been recorded yet.
            const fields = section.key === "communication" ? row.fields : this.historyFields(section.key, row);
            this.send({
                type: "OPEN_HISTORY",
                field: {
                    section: section.key,
                    itemId: row.itemId,
                    attributeId: fields.length ? fields[0].attributeId : null,
                    attributeIds: fields.map((field) => field.attributeId),
                },
            });
        }
        loadedSection(key) {
            return ClientModule.teamOperationSections[key] || null;
        }
        startMachine() {
            this.machineService.stop();
            ClientModule.ResetTeamOperations();
            this.machineService = interpret(this.machine.withContext({ ...this.machine.context, teamId: this.teamId }), { devTools: true });
            this.machineService.onTransition((state) => {
                this.state = state;
            });
            this.machineService.start();
        }
        // A different team opened while this tab stays mounted
        onTeamChange() {
            this.startMachine();
        }
        created() {
            this.startMachine();
        }
        beforeDestroy() {
            this.machineService.stop();
            ClientModule.ResetTeamOperations();
        }
    };
    __decorate([
        Prop({ required: true }),
        __metadata("design:type", String)
    ], TeamOperationsComponent.prototype, "teamId", void 0);
    __decorate([
        Watch("teamId"),
        __metadata("design:type", Function),
        __metadata("design:paramtypes", []),
        __metadata("design:returntype", void 0)
    ], TeamOperationsComponent.prototype, "onTeamChange", null);
    TeamOperationsComponent = __decorate([
        Component({
            name: "TeamOperationsComponent",
            components: {
                SectionLoader,
                TeamOperationsValueCell,
                TeamOperationsFieldHistoryModal,
                simplebar,
            },
        })
    ], TeamOperationsComponent);
    return TeamOperationsComponent;
})();
export default TeamOperationsComponent;
